import React from "react";
import Decimal from "decimal.js";
import { useDocument } from "../loan_tool";
import { useUser } from "../../../hooks/user";
import { formatPhone } from "./form_utils";
import Image from "../../ui/image";
import CurrencyField from "../../fields/currency_field";
import {
  loan_amount_first_mortgage,
  first_mortgage_payment_p_and_i,
  property_tax
} from "../../../calculations/loan";
import {
  home_owners_insurance_monthly,
  flood_insurance_monthly,
  hoa_dues_1,
  hoa_dues_2,
  mello_roos
} from "../../../calculations/property";
import { INTEREST_ONLY } from "../../../constants/loan_payment_type";
import { dscrPropertyPatch, dscrLoanPatch, dscrAddressCszPatch, dscrHoaPropertyPatch } from "../../../calculations/dscr";
import "./css/ba_forms.scss";
import "./css/dscr_form.scss";

function money(value) {
  return new Decimal(value || 0).toDecimalPlaces(0).toNumber().toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  });
}
function ratio(value) {
  return new Decimal(value || 0).toDecimalPlaces(3).toString();
}

export default function DscrCalculatorForm() {
  const [{ companyProfile }] = useUser();
  const [
    document,
    setDocument,
    { documentType, documentAccountProfile },
    actions
  ] = useDocument();

  const { applicant, property, property_to_be_sold, is_selling_property, loans } = document;
  const defaultBorrowerName = ((applicant && applicant.borrowers) || [])
    .map(b => [b.first_name, b.middle_name, b.last_name].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(", ");
  const borrowerName =
    document.dscr_form_borrower_name != null
      ? document.dscr_form_borrower_name
      : defaultBorrowerName;

  // Unify the rent roll: if the LO set up a NonQM DSCR income source, the rent
  // roll lives THERE (it drives the borrower's qualifying income), and this form
  // edits the very same one — so rents entered here show on the Income page and
  // vice-versa. If no DSCR income source exists, fall back to a document-level roll
  // so the form still works as a standalone quick-calc.
  const borrowers = (applicant && applicant.borrowers) || [];
  let dscrLoc = null;
  borrowers.forEach((b, bIndex) => {
    const sources = (b.income && b.income.sources) || [];
    sources.forEach((s, sIndex) => {
      if (dscrLoc == null && s.calculator === "NonQM DSCR") {
        dscrLoc = { bIndex, sIndex };
      }
    });
  });
  const rentRoll = dscrLoc
    ? borrowers[dscrLoc.bIndex].income.sources[dscrLoc.sIndex].dscr_rent_roll || [
        { address: "", rent: "0" }
      ]
    : document.dscr_rent_roll || [{ address: "", rent: "0" }];
  const totalRents = rentRoll.reduce(
    (accum, u) => accum.add(new Decimal((u && u.rent) || 0)),
    new Decimal(0)
  );

  function saveNow() {
    try {
      actions.saveDocument();
    } catch (e) {
      // non-fatal; the change stays on screen
    }
  }
  // Two-way write-back to the source pages (same as the Income-page calc).
  function editProperty(field, value, save = true) {
    setDocument({ ...document, property: { ...property, ...dscrPropertyPatch(documentType, field, value) } });
    if (save) saveNow();
  }
  function editPropertyRaw(patch) {
    setDocument({ ...document, property: { ...property, ...patch } });
    saveNow();
  }
  function editLoan(loanIndex, field, value, save = true) {
    const nextLoans = (loans || []).map((l, i) =>
      i === loanIndex ? { ...l, ...dscrLoanPatch(documentType, l, field, value) } : l
    );
    setDocument({ ...document, loans: nextLoans });
    if (save) saveNow();
  }
  function editHoa(loanIndex, value) {
    const nextLoans = (loans || []).map((l, i) =>
      i === loanIndex ? { ...l, ...dscrLoanPatch(documentType, l, "hoa", value) } : l
    );
    setDocument({
      ...document,
      loans: nextLoans,
      property: { ...property, ...dscrHoaPropertyPatch(value) }
    });
    saveNow();
  }
  // Write DSCR fields to the same place the rent roll lives (the DSCR income source
  // when one exists, else the document) so the Form and the Income page stay unified.
  // Text edits update in-memory on each keystroke and save on blur (no save-thrash /
  // focus loss); the currency field and Add/Remove save immediately.
  function applyDscr(patch, save) {
    let nextDoc;
    if (dscrLoc) {
      const nextBorrowers = borrowers.map((b, bi) => {
        if (bi !== dscrLoc.bIndex) return b;
        const nextSources = b.income.sources.map((s, si) =>
          si === dscrLoc.sIndex ? { ...s, ...patch } : s
        );
        return { ...b, income: { ...b.income, sources: nextSources } };
      });
      nextDoc = { ...document, applicant: { ...applicant, borrowers: nextBorrowers } };
    } else {
      nextDoc = { ...document, ...patch };
    }
    setDocument(nextDoc);
    if (save) saveNow();
  }
  function updateRoll(nextRoll, save) {
    applyDscr({ dscr_rent_roll: nextRoll }, save);
  }
  function onChangeUnit(index, field, value) {
    // Currency commits on change; free-text (notes) saves on blur.
    const save = field === "rent";
    updateRoll(
      rentRoll.map((u, i) => (i === index ? { ...u, [field]: value } : u)),
      save
    );
  }
  function addUnit() {
    updateRoll([...rentRoll, { notes: "", rent: "0" }], true);
  }
  function removeUnit(index) {
    updateRoll(rentRoll.filter((_, i) => i !== index), true);
  }

  // Property address is two-way with the SUBJECT property (the true source), so an
  // edit here flows to the New Property / Subject Property page and vice-versa.
  const addrStreet = (property && property.address) || "";
  const addrCsz =
    [property && property.city, property && property.state].filter(Boolean).join(", ") +
    (property && property.zip ? " " + property.zip : "");

  const scenarios = (loans || [])
    .map((loan, index) => ({ loan, index }))
    .filter(({ loan }) => new Decimal(loan.rate || 0).gt(0))
    .map(({ loan, index }) => {
      try {
        const args = [documentType, loan, property, property_to_be_sold, is_selling_property];
        const loanAmount = loan_amount_first_mortgage(...args);
        const pi = first_mortgage_payment_p_and_i(...args);
        const io = new Decimal(loanAmount).mul(new Decimal(loan.rate || 0).div(100)).div(12);
        const tax = property_tax(documentType, loan, property);
        const hoi = home_owners_insurance_monthly(documentType, property);
        const flood = flood_insurance_monthly(documentType, property);
        const hoa = hoa_dues_1(property, loan).add(hoa_dues_2(property, loan)).add(mello_roos(property, loan));
        const escrow = tax.add(hoi).add(flood).add(hoa);
        const pitia = pi.add(escrow);
        const itia = io.add(escrow);
        const dscrPitia = pitia.gt(0) ? totalRents.div(pitia) : new Decimal(0);
        const dscrItia = itia.gt(0) ? totalRents.div(itia) : new Decimal(0);
        return { index, loan, loanAmount, pi, io, tax, hoi, flood, hoa, pitia, itia, dscrPitia, dscrItia, interestOnly: loan.payment_type === INTEREST_ONLY };
      } catch (e) {
        const z = new Decimal(0);
        return { index, loan, loanAmount: z, pi: z, io: z, tax: z, hoi: z, flood: z, hoa: z, pitia: z, itia: z, dscrPitia: z, dscrItia: z, interestOnly: false };
      }
    });

  const belowStyle = { background: "#fdf3c7" };
  function dscrCell(dscr, key) {
    const below = new Decimal(dscr).lt(1);
    return (
      <td key={key} className="right-align num" style={below ? belowStyle : undefined}>
        {ratio(dscr)}
      </td>
    );
  }

  const companyName = companyProfile.company_name || "";
  const loName = [documentAccountProfile.first_name, documentAccountProfile.last_name]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="dscr-form">
      <div className="dscr-form-header">
        <div className="dscr-form-logo">
          <Image url="company/company-logo" className="dscr-form-logo-img" />
        </div>
        <div className="dscr-form-heading">
          <h1>NonQM DSCR Qualification Worksheet</h1>
          <div className="dscr-form-company">{companyName}</div>
          <div className="dscr-form-lo">
            {loName}
            {documentAccountProfile.email ? ` · ${documentAccountProfile.email}` : ""}
            {companyProfile.contact_phone
              ? ` · Phone: ${formatPhone(companyProfile.contact_phone, companyProfile.extension)}`
              : ""}
            {documentAccountProfile.nmls_number ? ` · NMLS# ${documentAccountProfile.nmls_number}` : ""}
          </div>
        </div>
      </div>

      <div className="dscr-form-borrower">
        <label>Borrower</label>
        <input
          type="text"
          className="dscr-input"
          value={borrowerName}
          onChange={e => setDocument({ ...document, dscr_form_borrower_name: e.target.value })}
          onBlur={saveNow}
          placeholder="Borrower name"
        />
      </div>

      <div className="dscr-address">
        <div className="dscr-address-row">
          <label>Property Address</label>
          <input
            key={"street-" + addrStreet}
            type="text"
            className="dscr-input"
            defaultValue={addrStreet}
            onBlur={e => editPropertyRaw({ address: e.target.value })}
            placeholder="Street address"
          />
        </div>
        <div className="dscr-address-row">
          <label>City, State, Zip</label>
          <input
            key={"csz-" + addrCsz}
            type="text"
            className="dscr-input"
            defaultValue={addrCsz}
            onBlur={e => editPropertyRaw(dscrAddressCszPatch(e.target.value))}
            placeholder="City, State  Zip"
          />
        </div>
      </div>

      <h3>Rents Received (100% of gross)</h3>
      <table className="dscr-rent-roll">
        <thead>
          <tr>
            <th className="dscr-unit-col">Unit</th>
            <th>Notes</th>
            <th className="right-align">Rent Received</th>
            <th className="no-print" />
          </tr>
        </thead>
        <tbody>
          {rentRoll.map((unit, index) => (
            <tr key={index}>
              <td className="dscr-unit-col">Rental Unit {index + 1}</td>
              <td>
                <input
                  type="text"
                  className="dscr-input"
                  value={unit.notes || ""}
                  onChange={e => onChangeUnit(index, "notes", e.target.value)}
                  onBlur={saveNow}
                />
              </td>
              <td className="right-align">
                <CurrencyField
                  fieldName="rent"
                  value={unit.rent}
                  onChange={obj => onChangeUnit(index, "rent", obj.rent)}
                />
              </td>
              <td className="no-print">
                {rentRoll.length > 1 && (
                  <button className="button" onClick={() => removeUnit(index)}>Remove</button>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td className="bold" colSpan="2">Total Rents Received</td>
            <td className="right-align num bold">{money(totalRents)}</td>
            <td className="no-print">
              <button className="button" onClick={addUnit}>Add Unit</button>
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Subject Property PITIA DSCR — by Loan</h3>
      {scenarios.length === 0 ? (
        <p className="dscr-note">
          Enter a rate on the LE Price Eng page for at least one loan to see the DSCR
          qualification.
        </p>
      ) : (
        <table className="dscr-results">
          <thead>
            <tr>
              <th />
              {scenarios.map(s => (
                <th key={s.index} className="right-align">Loan {s.index + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr><td>Rate <span className="dscr-edit-hint">(editable)</span></td>{scenarios.map(s => (<td key={s.index} className="right-align"><input type="text" className="dscr-input dscr-input-right" value={s.loan.rate || ""} onChange={e => editLoan(s.index, "rate", e.target.value, false)} onBlur={saveNow} /></td>))}</tr>
            <tr><td>Loan Amount (1st) <span className="dscr-edit-hint">(editable)</span></td>{scenarios.map(s => (<td key={s.index} className="right-align"><CurrencyField fieldName="v" value={s.loanAmount.toString()} onChange={obj => editLoan(s.index, "loanAmount", obj.v)} /></td>))}</tr>
            <tr><td>1st Loan Payment (P&amp;I)</td>{scenarios.map(s => (<td key={s.index} className="right-align num">{money(s.pi)}</td>))}</tr>
            <tr><td>Interest Only Payment</td>{scenarios.map(s => (<td key={s.index} className="right-align num">{money(s.io)}</td>))}</tr>
            <tr><td>Property Tax <span className="dscr-edit-hint">(editable)</span></td>{scenarios.map(s => (<td key={s.index} className="right-align"><CurrencyField fieldName="v" value={s.tax.toString()} onChange={obj => editProperty("tax", obj.v)} /></td>))}</tr>
            <tr><td>Home Owners Insurance <span className="dscr-edit-hint">(editable)</span></td>{scenarios.map(s => (<td key={s.index} className="right-align"><CurrencyField fieldName="v" value={s.hoi.toString()} onChange={obj => editProperty("hoi", obj.v)} /></td>))}</tr>
            <tr><td>Flood Insurance <span className="dscr-edit-hint">(editable)</span></td>{scenarios.map(s => (<td key={s.index} className="right-align"><CurrencyField fieldName="v" value={s.flood.toString()} onChange={obj => editProperty("flood", obj.v)} /></td>))}</tr>
            <tr><td>HOA / Mello Roos / Other <span className="dscr-edit-hint">(editable)</span></td>{scenarios.map(s => (<td key={s.index} className="right-align"><CurrencyField fieldName="v" value={s.hoa.toString()} onChange={obj => editHoa(s.index, obj.v)} /></td>))}</tr>
            <tr><td className="bold">Total PITIA</td>{scenarios.map(s => (<td key={s.index} className="right-align num bold">{money(s.pitia)}</td>))}</tr>
            <tr><td className="bold">Total ITIA</td>{scenarios.map(s => (<td key={s.index} className="right-align num bold">{money(s.itia)}</td>))}</tr>
            <tr><td className="bold">Subject Property PITIA DSCR</td>{scenarios.map(s => dscrCell(s.dscrPitia, s.index))}</tr>
            <tr><td className="bold">Subject Property ITIA DSCR</td>{scenarios.map(s => dscrCell(s.dscrItia, s.index))}</tr>
            <tr><td>Interest Only?</td>{scenarios.map(s => (<td key={s.index} className="right-align">{s.interestOnly ? "Yes" : "No"}</td>))}</tr>
          </tbody>
        </table>
      )}
      <p className="dscr-note">
        If the property cash flow qualifies (DSCR at or above the program minimum — most
        want 1.00+), the borrower qualifies. Anything under 1.00 is highlighted.
      </p>
    </div>
  );
}
